import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import RiskIndexInfoDrawerView from '@app/src/components/Drawer/Views/RiskIndexInfoDrawerView'
import SimpleRiskChipCell from '@app/src/components/Table/Cells/SimpleRiskChipCell'
import ValueCell from '@app/src/components/Table/Cells/ValueCell'
import Avatar, { AvatarSize } from '@app/src/components/Ui/Avatar'
import RiskIndexIcon from '@app/src/pages/Configurations/ConfigurationsPages/RiskScreening/RiskIndexIcons/RiskIndexIcon'
import EnvironmentIcon from '@app/src/pages/Dashboards/TransparencyDashboard/icons/EnvironmentIcon'
import GovernanceIcon from '@app/src/pages/Dashboards/TransparencyDashboard/icons/GovernanceIcon'
import SocialIcon from '@app/src/pages/Dashboards/TransparencyDashboard/icons/SocialIcon'
import type { RiskStatus } from '@app/src/types/resourceExplorer'
import { RiskIndexType, SustainabilityArea } from '@app/src/types/resourceExplorer'
import { resolveSubIndustry } from '@app/src/utils/providerEnrichmentUtils'
import { Factory } from '@mui/icons-material'
import { alpha, Box, Stack, TableCell, TableRow, Typography, useTheme } from '@mui/material'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import type { ProviderRiskRow } from './ProviderRisksPage'
import type { RiskScreeningInfoDialogProps } from './RiskScreeningInfoDialog'
import RiskScreeningInfoDialog from './RiskScreeningInfoDialog'

interface ProviderRisksPageRowProps {
  row: ProviderRiskRow
}

export const getESGIcon = (sustainabilityArea: string) => {
  switch (sustainabilityArea) {
    case SustainabilityArea.Environment:
      return <EnvironmentIcon />
    case SustainabilityArea.Social:
      return <SocialIcon />
    case SustainabilityArea.Governance:
      return <GovernanceIcon />
    default:
      return null
  }
}

export const getESGColor = (sustainabilityArea: string) => {
  switch (sustainabilityArea) {
    case SustainabilityArea.Environment:
      return 'success.light'
    case SustainabilityArea.Social:
      return 'warning.light'
    case SustainabilityArea.Governance:
      return 'info.light'
    default:
      return 'grey.300'
  }
}

const ProviderRisksPageRow: React.FC<ProviderRisksPageRowProps> = ({ row }) => {
  const { formatMessage } = useIntl()
  const isIndustryRisk = row.riskType.riskIndexType === RiskIndexType.Industry
  const [riskDialogContent, setRiskDialogContent] = useState<RiskScreeningInfoDialogProps['riskDialogContent']>()
  const { palette } = useTheme()
  const { openDrawer } = useDrawer()
  const subIndustry = resolveSubIndustry(row.parentObject)

  return (
    <>
      <TableRow>
        <TableCell>
          <Box display="flex" alignItems="center" gap={1}>
            <Avatar sx={{ bgcolor: getESGColor(row.sustainabilityArea), width: 32, height: 32 }}>
              {getESGIcon(row.sustainabilityArea)}
            </Avatar>
            <Typography color="text.primary">
              {formatMessage({ id: `schemas.risk.esgArea.${row.sustainabilityArea}` })}
            </Typography>
          </Box>
        </TableCell>
        <TableCell>
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{
              cursor: 'pointer',
            }}
            onClick={() => openDrawer(<RiskIndexInfoDrawerView riskType={row.riskType} />)}
          >
            <Avatar
              size={AvatarSize.Medium}
              sx={{
                backgroundColor: isIndustryRisk ? alpha(palette.secondary.main, 0.1) : 'white',
                mr: 1,
              }}
            >
              {isIndustryRisk ? <Factory color="secondary" /> : <RiskIndexIcon source={row.riskType.source} />}
            </Avatar>

            <Typography color="text.primary" sx={{ '&:hover': { textDecoration: 'underline' } }}>
              {row.riskType.name}
            </Typography>
          </Stack>
        </TableCell>
        <SimpleRiskChipCell
          riskStatus={row.severity as RiskStatus}
          onClick={() => {
            setRiskDialogContent({
              providerName: row.parentObject.name,
              countryName: row.parentObject.country.name,
              location: row.context,
              risk: row,
              industry: subIndustry?.description,
              industryCode: subIndustry?.code,
            })
          }}
        />
        <ValueCell value={formatMessage({ id: `schemas.risk.context.${row.context}` })} />
      </TableRow>
      <RiskScreeningInfoDialog
        riskDialogContent={riskDialogContent}
        setRiskDialogContent={setRiskDialogContent}
        isIndustry={isIndustryRisk}
      />
    </>
  )
}

export default ProviderRisksPageRow
